import { isValidElement, useEffect } from "react";
import Radio from "backbone.radio";
import { noop } from "lodash-es";

import { AuthRoute, useRouteMatch, useParams, Switch, Redirect, useQueryParameters } from "extensions/react-router";

import { RESET_COMPARISON_DATES, SET_ACTIVE_QUALIFIERS } from "common/actions/common";
import strings from "common/strings";
import { store } from "common/store";
import { ensureErrorCallback } from "common/components/error";
import { toast } from "extensions/toast";
import { dataTypes } from "common/actions/dataAvailability";
import { pazienHistory } from "common/components/pazienHistory";
import { UPDATE_USER_MERCHANT_SETTINGS } from "common/actions/user_merchant_settings";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faQuestionCircle } from "@fortawesome/pro-regular-svg-icons";

const ShowReactVersion = store.getState().settings.showAnalyticsReact;

// When navigating via a report, each analytics feature should behave slightly differently than when navigating directly.
const reportViewProps = {
    section: strings.page_reports,
    sidebar_name: strings.page_reports,
    asyncHeaderBar(cb) {
        return this.configPromise.bind(this).then((data) => {
            // Return earlier if the view already destroyed
            if (this.isDestroyed()) return;
            
            if (["comparison", "benchmarks"].includes(data.type)) {
                return cb({
                    name: data.name,
                    dates: data.dates
                });
            }
            
            return cb({
                name: this.config.get("name"),
                dates: this.config.get("dates")
            });
        });
    }
};

/*
  Common setup for every product in analytics, handles report logic, loads the relevant component, and renders it.
  */
function AnalyticsComponent({ componentResolver, initialProps, setup = noop }) {
    const { id: reportId } = useParams();
    const { options, qualifiers = "", ...queryParams } = useQueryParameters();
    const display_options = options || null;
    const reportNotAllowed = reportId && !store.getState().permissions.user_permissions.has_saved_reports;

    useEffect(() => {
        if (!reportNotAllowed) {
            try {
                setup();
                SET_ACTIVE_QUALIFIERS(qualifiers);
                // Every analytics product can be part of a report when requested.
                if (reportId && store.getState().common.historyStack.length !== 1) {
                    store.dispatch(RESET_COMPARISON_DATES());
                }
                const viewProps = {
                    ...initialProps,
                    ...reportId && reportViewProps,
                    display_options,
                    qualifiers,
                    queryParams,
                    reportId
                };

                // About to load a new chunk, show loading screen until it can actually render.
                Radio.channel("main").request("show:loading:view", viewProps);
                
                // Load the chunk and render that component.
                componentResolver(viewProps).then((Component) => {
                    if (isValidElement(Component)) {
                        Radio.channel("main").request("show:app:view", Component).catch(ensureErrorCallback);
                    } else {
                        Radio.channel("main").request("show:app:view", new Component(viewProps)).catch(ensureErrorCallback);
                    }
                });
            } catch (error) {
                ensureErrorCallback(error);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [display_options, qualifiers, reportId, queryParams]);

    return reportNotAllowed ? <Redirect to="/merchant/dashboard" /> : null;
}

const Chargebacks = {
    initialProps: {
        section_id: "chargebacks",
        section: strings.page_analytics,
        name: strings.page_chargebacks,
        dataAvailabilityType: [dataTypes.CHARGEBACKS]
    },
    componentResolver: async() => {
        const { ChargebacksMainView } = await import(
            /* webpackChunkName: "chargebacksMainView" */
            "./chargebacks/chargebacksMainView"
        );
        return ChargebacksMainView;
    }
};

const Comparison = {
    initialProps: {
        name: "Comparisons", // We should set name of a report here when it becomes available
        section: strings.page_analytics
    },
    componentResolver: async() => {
        const { ComparisonView } = await import(
            /* webpackChunkName: "comparisonView" */
            "./common/comparison/comparisonView"
        );
        return ComparisonView;
    }
};

let Authorizations;
if (!ShowReactVersion) {
    Authorizations = {
        initialProps: {
            section_id: "authorizations",
            name: strings.page_authorizations,
            section: strings.page_analytics,
            dataAvailabilityType: [dataTypes.AUTHORIZATIONS]
        },
        componentResolver: async() => {
            const { AuthorizationsMainView } = await import(
                /* webpackChunkName: "authorizationsMainView" */
                "./authorizations/authorizationsMainView"
            );
            return AuthorizationsMainView;
        }
    };
} else {
    Authorizations = {
        initialProps: {},
        componentResolver: async(opts) => {
            const { AuthorizationsPage } = await import(
                /* webpackChunkName: "authorizationsMainView" */
                "./authorizations/authorizationsReactPage"
            );
            return <AuthorizationsPage options={opts} />;
        }
    };
}

const Cashflow = {
    initialProps: {
        section_id: "cashflow",
        section: strings.page_analytics,
        name: strings.page_cashflow,
        dataAvailabilityType: [dataTypes.SETTLEMENTS, dataTypes.CHARGEBACKS, dataTypes.FEES]
    },
    componentResolver: async() => {
        const { CashFlowMainView } = await import(
            /* webpackChunkName: "cashFlowMainView" */
            "./cashflow/cashFlowMainView"
        );
        return CashFlowMainView;
    }
};

const showFeeReportNotification = () => {
    const state = store.getState();
    if (state.common.user.optIns.feeReports && !state.user_merchant_settings.fee_reports_dismissed) {
        const remindMeLater = () => {
            toast.dismiss();
        };
        const dismiss = () => {
            const data = state.user_merchant_settings;
            store.dispatch(UPDATE_USER_MERCHANT_SETTINGS({
                ...data,
                fee_reports_dismissed: true
            }));
            toast.dismiss();
        };
        const learnMore = () => {
            dismiss();
            pazienHistory.navigateWithParams("preferences/preferences", { trigger: true });
        };
        if (state.common.user.optIns.feeReports.active) {
            dismiss();
        } else {
            const notification_content = (
                <div>
                    <FontAwesomeIcon icon={faQuestionCircle}/>&nbsp;
                    &nbsp;Worldpay offers monthly transaction-level fee reports upon request.
                    &nbsp;<button className="learnMore" type="button" onClick={learnMore} style={{ "margin-left": "15px" }}><a>Learn more</a> </button>
                    &nbsp;<button className="remindLater" type="button" onClick={remindMeLater} style={{ "margin-left": "100px" }}>Remind me later </button>
                    &nbsp;<button className="noThanks" type="button" onClick={dismiss}> No thanks</button>

                </div>
            );
            toast.info(notification_content, { autoClose: false, closeOnClick: false });
        }
    }
};

const Fees = {
    initialProps: {
        section_id: "fees",
        section: strings.page_analytics,
        name: strings.page_fees,
        dataAvailabilityType: [dataTypes.FEES]
    },
    componentResolver: async() => {
        const { FeesMainView } = await import(
            /* webpackChunkName: "feesMainView" */
            "./fees/feesMainView"
        );
        return FeesMainView;
    },
    setup: showFeeReportNotification
};

const Settlements = {
    initialProps: {
        section_id: "settlements",
        section: strings.page_analytics,
        name: strings.page_sales_refunds,
        dataAvailabilityType: [dataTypes.SETTLEMENTS]
    },
    componentResolver: async() => {
        const { SettlementsMainView } = await import(
            /* webpackChunkName: "settlementsMainView" */
            "./settlements/settlementsMainView"
        );
        return SettlementsMainView;
    }
};

const Benchmarks = {
    initialProps: {
        name: "Benchmarks",
        asyncHeaderBar(cb) {
            // On page load, the default suggested date range will show the last 3 full months
            let dates;
            const isBeginningOfMonth = new Date().getDate() < 5;
            if (store.getState().common.historyStack.length !== 1) {
                dates = isBeginningOfMonth ? "now-4M/M_now-2M/M" : "now-3M/M_now-1M/M";
            }
            const maxDate = isBeginningOfMonth ? "now-2M/M" : "now-1M/M";

            return cb({
                name: "Benchmarks",
                dates,
                maxDate,
                calendars: ["month"],
                datePickerId: "benchmarks"
            });
        }
    },
    componentResolver: async() => {
        const { BenchmarksView } = await import(
            /* webpackChunkName: "benchmarksView" */
            "./common/benchmarks/benchmarksView"
        );
        return BenchmarksView;
    }
};

let AllFraud;
if (!ShowReactVersion) {
    AllFraud = {
        initialProps: {
            section_id: "all_fraud",
            section: strings.page_fraud,
            name: strings.page_allfraud,
            dataAvailabilityType: [dataTypes.FRAUD],
            asyncHeaderBar: store.getState().common.historyStack.length === 1 ? void 0 : function(cb) {
                // On page load, the default suggested date range will show the last 3 full months + the current month to date
                return cb({
                    name: strings.page_allfraud,
                    dates: "now-3M/M_now",
                    datePickerId: "fraud"
                });
            }
        },
        componentResolver: async() => {
            const { FraudMainView } = await import(
                /* webpackChunkName: "fraudMainView" */
                "./fraud/all/fraudMainView"
            );
            return FraudMainView;
        }
    };
} else {
    AllFraud = {
        initialProps: {
            section_id: "all_fraud",
            section: strings.page_fraud,
            name: strings.page_allfraud,
            datePickerInfo: {
                dataAvailabilityType: [dataTypes.FRAUD],
                dateComparison: true,
                dates: "now-3M/M_now",
                datePickerId: "fraud",
                datetypes: [
                    {
                        name: "Transaction date",
                        value: "txn_date",
                        selected: true
                    }, {
                        name: "Fraud report date",
                        value: "activity",
                        tooltip: "The EU regulation on SCA uses the reported fraud date to calculate the pertinent fraud rate, therefore we limit this page's view to just fraud notice date."

                    }]
            }
        },
        componentResolver: async(opts) => {
            const { AllFraudPage } = await import(
                /* webpackChunkName: "fraudMainView" */
                "./fraud/all/allFraudPage"
            );
            return <AllFraudPage options={opts} />;
        }
    };
}

const FlexFraud = {
    initialProps: {
        section_id: "threedsflex",
        section: "Fraud tools",
        name: "3DS authentication",
        dataAvailabilityType: [dataTypes.FRAUD],
        dateTypes: () => Promise.resolve({
            datetypes: [{
                name: "Transaction date",
                value: "activity",
                selected: true
            }]
        }),
        asyncHeaderBar(cb) {
            // On page load, the default suggested date range will show the last 2 quarters
            let dates;
            if (store.getState().common.historyStack.length !== 1) {
                dates = "now-30d_now";
            }
            return cb({
                name: "3DS authentication",
                dates,
                datePickerId: "threedsflex"
            });
        }
        
    },
    
    componentResolver: async(opts) => {
        const { FlexPage } = await import(
            /* webpackChunkName: "fraudMainView" */
            "./fraud/authentication/flexPage"
        );
        return <FlexPage options={opts} />;
    }
};

const Fraudsight = {
    initialProps: {
        section_id: "fraudsight",
        section: "Fraud tools",
        name: "Risk monitoring (BETA)",
        dataAvailabilityType: [dataTypes.FRAUD],
        dateTypes: () => Promise.resolve({
            datetypes: [{
                name: "Transaction date",
                value: "activity",
                selected: true
            }]
        }),
        asyncHeaderBar(cb) {
            // On page load, the default suggested date range will show the last 2 quarters
            let dates;
            if (store.getState().common.historyStack.length !== 1) {
                dates = "now-30d_now";
            }
        
            return cb({
                name: "Risk monitoring (BETA)",
                dates,
                datePickerId: "fraudsight"
            });
        }
    },
    componentResolver: async(opts) => {
        const { FraudsightPage } = await import(
            /* webpackChunkName: "fraudMainView" */
            "merchant/features/analytics/fraud/fraudsight/fraudsightPage"
        );
        return <FraudsightPage options={opts} />;
    }
};

const Psd2Fraud = {
    initialProps: {
        section_id: "psd2_fraud",
        section: strings.page_fraud,
        name: strings.page_psd2,
        dataAvailabilityType: [dataTypes.FRAUD],
        dateTypes: () => Promise.resolve({
            datetypes: [{
                name: "PSD2 Reporting period",
                value: "activity",
                selected: true,
                tooltip: "Quarterly, and based on the relevant fraudulent transactions executed and reported in a given calendar quarter"
            }]
        }),
        asyncHeaderBar(cb) {
            // On page load, the default suggested date range will show the last 2 quarters
            let dates;
            if (store.getState().common.historyStack.length !== 1) {
                dates = "now-1Q/Q_now/Q";
            }
            return cb({
                name: "PSD2 reporting period",
                dates,
                calendars: ["quarter"],
                datePickerId: "PSD2"
            });
        }
    },
    
    componentResolver: async(opts) => {
        const { PSD2Page } = await import(
            /* webpackChunkName: "fraudMainView" */
            "merchant/features/analytics/fraud/psd2q/psd2Page"
        );
        return <PSD2Page options={opts} />;
    }
};

const authenticated = (permission) => (state) => state.permissions.user_permissions[permission];

const createAnalyticsRoute = ({ url, path, component, permission }) => (
    <>
        {/*
           Due to https://github.com/Pazien/web-application/issues/4768 (A limitation of Amazon CloudFront),
           as of release 1.126, the analytics URLs no longer put display options in the `options` part of the URL path.
           Instead, they should be in the query string as `options=${options}`.

           This route remaps the old URL format to the new one (handled above).
        */}
        <AuthRoute
            path={[`${url}/analytics/${path}/:options?/:qualifiers?`, `${url}/reports/${path}/:id/:options?/:qualifiers?`]}
            authenticated={authenticated(permission)}
        >
            {({ match }) => (
                match
                    ? (match.params.qualifiers || match.params.options
                        ? (
                            <Redirect
                                to={pazienHistory.addParamsToFragment(
                                    `${match.path.split("/:options?/:qualifiers?")[0]}`,
                                    { ...pazienHistory.parseQueryString(), options: match.params.options, qualifiers: match.params.qualifiers }
                                )}
                            />
                        )
                        : <AnalyticsComponent {...component} />)
                    : null
            )}
        </AuthRoute>
    </>
);

export function AnalyticsRouter() {
    const { url } = useRouteMatch();

    return (
        <Switch>
            {createAnalyticsRoute({
                url, path: "authorizations", permission: "data_access_authorizations", component: Authorizations
            })}
            {createAnalyticsRoute({
                url, path: "cashflow", permission: "has_page_cashflow", component: Cashflow
            })}
            {createAnalyticsRoute({
                url, path: "fees", permission: "data_access_fees", component: Fees
            })}
            {createAnalyticsRoute({
                url, path: "chargebacks", permission: "data_access_chargebacks", component: Chargebacks
            })}
            {createAnalyticsRoute({
                url, path: "settlements", permission: "data_access_sales_refunds", component: Settlements
            })}
            {createAnalyticsRoute({
                url, path: "all_fraud", permission: "has_fraud", component: AllFraud
            })}
            {createAnalyticsRoute({
                url, path: "psd2_fraud", permission: "has_fraud", component: Psd2Fraud
            })}
            {createAnalyticsRoute({
                url, path: "3ds", permission: "has_fraud", component: FlexFraud
            })}
            {createAnalyticsRoute({
                url, path: "comparison", permission: "has_comparison", component: Comparison
            })}
            {createAnalyticsRoute({
                url, path: "benchmarks", permission: "has_benchmarks", component: Benchmarks
            })}
            {createAnalyticsRoute({
                url, path: "fraudsight", permission: "feature_fraudsight", component: Fraudsight
            })}
            
            {/* Any other (invalid) analytics route should navigate to authorizations by default */}
            <AuthRoute exact path={`${url}/analytics`}>
                <Redirect to={`${url}/analytics/authorizations`} />
            </AuthRoute>
        </Switch>
    );
}
